<template>
  <div class="product-variant-description">
    <div class="product-fabric-img" v-if="fabric.imageUrl" @click="onImageClick">
      <img :src="fabric.imageUrl" :alt="fabric.name" />
      <div class="expand-image-btn"></div>
    </div>
    <div class="product-fabric-text">
      <BackstageTooltip v-if="isSneakerProgram(fabric)" :text="$t('matrix.sneaker_program_tooltip_title')">
        <p class="strong sneaker-program">{{ fabric.name }}</p>
      </BackstageTooltip>
      <p v-else class="strong">{{ fabric.name }}</p>
      <p>{{ fabric.description }}</p>
    </div>
    <div v-if="fabric.launchDate" class="product-launch-text">
      <div v-if="showLaunchDate">
        <p class="strong">{{ t('matrix.product_launch_date') }}</p>
        <p>{{ parseUtcDateToLocaleDate(fabric.launchDate) }}</p>
      </div>
      <div v-else>
        <!-- TODO: proposal, initialDates later should be all dates where have this product -->
        <MultiDateCalendar
          :launchDate="fabric.launchDate"
          :preOrderEarliestRequestedDate="fabric.preOrderEarliestRequestedDate!"
          :initialDates="[new Date(fabric.preOrderEarliestRequestedDate!)]"
          @update:dates="onDatesSelected" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import BackstageTooltip from '@/components/common/BackstageTooltip.vue';
import { useFormat } from '@/composables/format';
import { useMatrixProduct } from '@/composables/useMatrixProduct';
import { FeaturesService } from '@/services/features.service';
import type { Fabric } from '@/types/matrix.interface';
import { MatrixType, TargetedAssortmentEnum } from '@onrunning/backstage-models';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import MultiDateCalendar, { type UpdateDatesProps } from '../common/MultiDateCalendar.vue';

const props = defineProps<{
  fabric: Fabric;
}>();

const emit = defineEmits(['show-product-model-details']);
const { parseUtcDateToLocaleDate } = useFormat();
const matrixProduct = useMatrixProduct();
const { t } = useI18n();

function onImageClick() {
  emit('show-product-model-details', props.fabric);
}

function isSneakerProgram(fabric: Fabric): boolean {
  return fabric.targetedAssortments?.includes(TargetedAssortmentEnum.SNEAKER_PROGRAM) ?? false;
}

const onDatesSelected = ({ dates, copyQuantity }: UpdateDatesProps) => {
  // TODO these 2 values we might emit up to parent when integrate with group by shipping date
  console.log('copyQuantity', copyQuantity);
  console.log('dates', dates);
};

const showLaunchDate = computed(() => {
  return !FeaturesService.enabledShipDatesGrouping || matrixProduct.matrixType.value !== MatrixType.preorder;
});
</script>

<style lang="scss" scoped>
.product-variant-description {
  .product-fabric-text p {
    margin: 0;
    font-size: 14px;
    text-align: left;
    color: var(--primary-text-remark-color);
  }

  .product-fabric-text p.strong {
    &.sneaker-program {
      color: var(--vivid-blue-color);
    }
  }

  .product-launch-text {
    margin-left: auto;
    padding: 17px 10px;
    min-width: 82px;
    border-left: 1px solid var(--bg-page-color);

    p {
      margin: 0;
      text-align: center;
      margin-left: auto;
      font-size: 14px;
      line-height: 150%;
      color: var(--primary-text-remark-color);
    }
  }

  .product-launch-text p.strong,
  .product-fabric-text p.strong {
    color: var(--primary-text-color);
    font-weight: 700;
    font-size: 16px;
  }

  .product-fabric-img {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    height: 80px;
    width: 110px;
    cursor: pointer;

    .expand-image-btn {
      display: block;
    }

    @include breakpoint(medium) {
      .expand-image-btn {
        width: 15px;
        height: 15px;
        top: 0;
        right: 0;
      }
    }

    img {
      display: block;
      max-width: 80px;
      max-height: 70px;
      width: auto;
      height: auto;
      object-fit: contain;
      margin: 0px 15px;
    }
  }

  @media (max-width: 1000px) {
    img {
      margin: 0 5px;
    }

    .product-fabric-text {
      word-break: break-word;
      padding: 0;
      margin: 0 5px;
      min-width: auto;

      p {
        font-size: 10px;
      }
    }

    .product-launch-text {
      padding: 0;
      margin: 0 5px;
      min-width: auto;

      p {
        font-size: 10px;
      }
    }
  }

  @include breakpoint(medium) {
    img {
      width: 60px;
      height: 35px;
      margin: 0;
      text-align: left;
    }

    .product-launch-text {
      padding: 0;
      min-width: auto;
    }

    .product-launch-text p,
    .product-fabric-text p {
      font-size: 10px;
      text-align: left;
    }

    .product-launch-text p.strong,
    .product-fabric-text p.strong {
      font-size: 10px;
      text-align: left;
    }
  }
}
</style>
